// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TitleWithTooltip from "src/components/shared/text/TitleWithTooltip/TitleWithTooltip";
import ExamCardButton from "src/components/containers/shared/ExamSliderList/ExamSliderCard/exam-cards/card-ui/ExamCardButton/ExamCardButton";
import TextInputField from "src/components/shared/inputs/TextInputField/TextInputField";
import CustomCheckbox from "src/components/shared/inputs/CustomCheckbox/CustomCheckbox";
import NumberInputField from "src/components/shared/inputs/NumberInputField/NumberInputField";
import ExamCardModal from "src/components/shared/modals/ExamCardModal/ExamCardModal";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";
import { SelectChangeEvent } from "@mui/material/Select";

// Styles
import {
  ModalCustomCancel,
  ModalCustomConfirm,
} from "src/styles/mui/muiStylesHelper";

// Hooks
import { useIOLLensTypes } from "src/services/clinics/hooks/clinic-lenses/iol-lenses/useIOLLensTypes";

// Types
import { AddEditIOLLensType, IOLLensType } from "src/services/clinics/types";

// Utils
import {
  IOL_ENUM_FIELDS,
  IOL_TORIC_CYLINDER,
} from "src/utils/exams/lensSimulationHelper";

const DEFAULT_LENS_VALUES: Record<string, Record<string, string>> = {
  Monofocal_Near: {
    halosType: "None",
    starburstsType: "None",
    glaresType: "None",
    reducedContrastType: "None",
  },
  Monofocal_Mid: {
    halosType: "None",
    starburstsType: "None",
    glaresType: "None",
    reducedContrastType: "None",
  },
  Monofocal_Far: {
    halosType: "None",
    starburstsType: "None",
    glaresType: "None",
    reducedContrastType: "None",
  },
  Edof: {
    halosType: "Mild",
    starburstsType: "Mild",
    glaresType: "Mild",
    reducedContrastType: "Mild",
  },
  Trifocal: {
    halosType: "Moderate",
    starburstsType: "Mild",
    glaresType: "Moderate",
    reducedContrastType: "Mild",
  },
};

type AddEditIOLModalType = {
  open: boolean;
  title: string;
  onClose: () => void;
  onConfirm: (lensData: AddEditIOLLensType) => void;
  isUpdating: boolean;
  lensToEdit?: IOLLensType | undefined;
};

const AddEditIOLModal = ({
  open,
  title,
  onClose,
  onConfirm,
  isUpdating,
  lensToEdit,
}: AddEditIOLModalType) => {
  const { t } = useTranslation();

  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [lensType, setLensType] = useState({ displayName: "", value: "" });
  const [toricCylinderValue, setToricCylinderValue] = useState("");
  const [showToric, setShowToric] = useState(false);
  const [enumValues, setEnumValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      IOL_ENUM_FIELDS.map((field) => [field.parameterName, ""])
    )
  );

  useEffect(() => {
    if (lensToEdit && open) {
      setName(lensToEdit.name);
      setPrice(lensToEdit.price ?? "");
      setLensType({
        displayName: t(lensToEdit.lensType),
        value: lensToEdit.lensType,
      });
      setToricCylinderValue(
        lensToEdit.toricCylinderType !== null &&
          lensToEdit.toricCylinderType !== undefined
          ? String(lensToEdit.toricCylinderType)
          : ""
      );
      setShowToric(
        lensToEdit.toricCylinderType !== null &&
          lensToEdit.toricCylinderType !== undefined
      );
      setEnumValues(
        Object.fromEntries(
          IOL_ENUM_FIELDS.map((field) => {
            const currentValue = lensToEdit[field.parameterName];
            const isValid = (field.options as readonly string[]).includes(
              currentValue
            );
            return [field.parameterName, isValid ? currentValue : ""];
          })
        )
      );
    } else if (!lensToEdit && open) {
      setToricCylinderValue("");
      setEnumValues(
        Object.fromEntries(
          IOL_ENUM_FIELDS.map((field) => [
            field.parameterName,
            field.options[0],
          ])
        )
      );
    }
  }, [lensToEdit, open, t]);

  const { data: lensTypeData, isLoading: isLoadingLensTypes } =
    useIOLLensTypes();

  const formattedLensOptions = useMemo(
    () =>
      (lensTypeData ?? [])?.map((data) => ({
        displayName: t(data),
        value: data,
      })),
    [t, lensTypeData]
  );

  const handleSetLensOption = useCallback(
    (event: SelectChangeEvent<any>) => {
      const value = formattedLensOptions?.find(
        (option) => option.displayName === event.target.value
      );

      if (value) {
        setLensType({ ...value });

        // set default values when first selecting a lens type
        const defaultEnumValues = DEFAULT_LENS_VALUES[value.value];
        if (defaultEnumValues) {
          setEnumValues(defaultEnumValues);
        }
      }
    },
    [formattedLensOptions]
  );

  const shouldDisable = useMemo(
    () => !name || !lensType.value || (showToric && !toricCylinderValue),
    [name, lensType.value, showToric, toricCylinderValue]
  );

  const resetValues = useCallback(() => {
    setName("");
    setPrice("");
    setLensType({ displayName: "", value: "" });
    setShowToric(false);
    setToricCylinderValue("");
    setEnumValues(
      Object.fromEntries(
        IOL_ENUM_FIELDS.map((field) => [field.parameterName, ""])
      )
    );
  }, []);

  const handleClose = useCallback(() => {
    resetValues();
    onClose();
  }, [resetValues, onClose]);

  const handleConfirm = useCallback(() => {
    const lensData = {
      name,
      price,
      lensType: lensType.value,
      toricCylinderType:
        toricCylinderValue === "" ? null : Number(toricCylinderValue),
      ...Object.fromEntries(
        IOL_ENUM_FIELDS.map((field) => [
          field.parameterName,
          enumValues[field.parameterName],
        ])
      ),
    } as AddEditIOLLensType;
    resetValues();
    onConfirm(lensData);
  }, [
    name,
    price,
    lensType.value,
    toricCylinderValue,
    enumValues,
    resetValues,
    onConfirm,
  ]);

  const textStyle = {
    color: "#FFF",
    fontSize: "12px",
  };

  const tooltipStyle = {
    color: "#FFF",
    fontSize: "12px",
    width: "16px",
  };

  return (
    <ExamCardModal
      backgroundColor="#202338"
      isOpen={open}
      title={title}
      onClose={handleClose}
      maxWidth="xs"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
          }}
        >
          <Button sx={ModalCustomCancel} onClick={handleClose}>
            {t("Cancel")}
          </Button>
          <Button
            data-testid="button-save"
            disabled={shouldDisable}
            sx={{
              ...ModalCustomConfirm(),
              "&.Mui-disabled": {
                color: "#FFF",
                backgroundColor: "#435B6D7A",
              },
            }}
            onClick={handleConfirm}
          >
            {t("Save")}
          </Button>
        </Grid>
      }
    >
      <Grid
        display="flex"
        flexDirection="column"
        p="8px"
        gap="8px"
        sx={{ backgroundColor: "#202338" }}
      >
        {/* Row 1: Name + Lens Type */}
        <Box display="flex" gap="8px">
          <Box
            display="flex"
            flexDirection="column"
            gap="4px"
            sx={{ flex: 1, minWidth: 0 }}
          >
            <Typography color="#FFF" fontSize="12px">
              {t("Name")}*
            </Typography>
            <TextInputField
              id="iol-name-input"
              required
              disabled={isLoadingLensTypes || isUpdating}
              autoFocus={!lensToEdit}
              placeholder={t("Enter a value")}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            gap="4px"
            sx={{ flex: 1, minWidth: 0 }}
          >
            <TitleWithTooltip
              title={`${t("Lens Type")}*`}
              tooltip={
                <div>{t("Sets the lens design and default values.")}</div>
              }
              titleStyle={textStyle}
              tooltipStyle={tooltipStyle}
            />
            <SelectDropdown
              id="iol-lens-type-dropdown"
              disabled={isLoadingLensTypes || isUpdating}
              dropdownOptions={formattedLensOptions}
              value={lensType.displayName}
              handleChange={handleSetLensOption}
            />
          </Box>
        </Box>

        {/* Row 2: Price */}
        <Box display="flex" gap="8px">
          <Box
            display="flex"
            flexDirection="column"
            gap="4px"
            sx={{ flex: 1, minWidth: 0 }}
          >
            <Typography color="#FFF" fontSize="12px">
              {t("Price")}
            </Typography>
            <TextInputField
              id="iol-price-input"
              required
              disabled={isLoadingLensTypes || isUpdating}
              placeholder={t("Enter a value")}
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
          </Box>
        </Box>

        {/* Severity fields: Halos, Starbursts, Glare, Reduced Contrast --
            1 per line. */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {IOL_ENUM_FIELDS.map(({ parameterName, title, tooltip, options }) => (
            <Box
              key={parameterName}
              display="flex"
              flexDirection="column"
              gap="4px"
            >
              <TitleWithTooltip
                title={`${t(title)}*`}
                tooltip={<div>{t(tooltip)}</div>}
                titleStyle={textStyle}
                tooltipStyle={tooltipStyle}
              />
              <Box display="flex" gap="4px">
                {options.map((option) => (
                  <ExamCardButton
                    key={`${parameterName}-${option}`}
                    label={t(option)}
                    isSelected={enumValues[parameterName] === option}
                    updateValue={() =>
                      setEnumValues((prev) => ({
                        ...prev,
                        [parameterName]: option,
                      }))
                    }
                    isDisabled={isLoadingLensTypes || isUpdating}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          gap="4px"
          sx={{ flex: 1, minWidth: 0 }}
        >
          <Box display="flex" alignItems="center" gap="8px">
            <CustomCheckbox
              id="toric-checkbox"
              checked={showToric}
              onClick={() => {
                setShowToric((prev) => {
                  const newVal = !prev;
                  if (newVal) {
                    setToricCylinderValue("1");
                  } else {
                    setToricCylinderValue("");
                  }
                  return newVal;
                });
              }}
              disabled={isLoadingLensTypes || isUpdating}
              checkedFill="#E2772E"
              checkedStroke="#FFF"
              uncheckedFill="#FFF"
              uncheckedStroke="#5B6D7A"
            />
            <Typography color="#FFF" fontSize="12px">
              {t("Toric")}
            </Typography>
          </Box>
        </Box>

        {/* Row 4: Toric Cylinder */}
        {Boolean(showToric) && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            <Box
              display="flex"
              flexDirection="column"
              gap="4px"
              sx={{ flex: 1, minWidth: 0 }}
            >
              <TitleWithTooltip
                title={t(IOL_TORIC_CYLINDER.title)}
                tooltip={<div>{t(IOL_TORIC_CYLINDER.tooltip)}</div>}
                titleStyle={textStyle}
                tooltipStyle={tooltipStyle}
              />
              <NumberInputField
                id="iol-toric-cylinder-input"
                disabled={isLoadingLensTypes || isUpdating}
                placeholder={t("Enter a value")}
                value={toricCylinderValue}
                min={IOL_TORIC_CYLINDER.min}
                max={IOL_TORIC_CYLINDER.max}
                onChange={(e) => setToricCylinderValue(e.target.value ?? "")}
              />
            </Box>
          </Box>
        )}
      </Grid>
    </ExamCardModal>
  );
};

export default AddEditIOLModal;
